import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchBar.css';
import { searchUsers, followUser, unfollowUser, getFollowing } from '../services/api';

function SearchBar({ onSearch, loading, onFocus }) {
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState('title'); // "title", "author", or "user"
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [followingIds, setFollowingIds] = useState([]);
  const navigate = useNavigate();
  
  // Get userId from localStorage
  const userId = Number(localStorage.getItem('userId'));

  // Fetch the current user's following list when component mounts
  useEffect(() => {
    if (userId) {
      fetchFollowingIds();
    }
  }, [userId]);

  // Function to fetch following ids
  const fetchFollowingIds = async () => {
    try {
      const followingList = await getFollowing(userId);
      setFollowingIds(followingList.map(u => u.id));
    } catch (err) {
      console.error('Failed to fetch following list:', err);
    }
  };

  const handleSearch = () => {
    if (!query.trim()) return;
    
    if (searchMode === 'user') {
      handleUserSearch();
    } else {
      let formattedQuery = query.trim();
      if (searchMode === 'title') {
        formattedQuery = `intitle:${formattedQuery}`;
      } else if (searchMode === 'author') {
        formattedQuery = `inauthor:${formattedQuery}`;
      }
      onSearch(formattedQuery);
      setShowResults(false);
    }
  };

  const handleUserSearch = async () => {
    if (query.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    
    setSearchingUsers(true);
    try {
      const results = await searchUsers(query.trim());
      const filtered = results.filter((u) => u.id !== userId);
      setSearchResults(filtered);
      setShowResults(true);
      
      // Refresh following status after search to ensure it's up to date
      await fetchFollowingIds();
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFollowToggle = async (targetId) => {
    try {
      const isFollowing = followingIds.includes(targetId);
      
      if (isFollowing) {
        await unfollowUser(userId, targetId);
        setFollowingIds((prev) => prev.filter((id) => id !== targetId));
      } else {
        await followUser(userId, targetId);
        setFollowingIds((prev) => [...prev, targetId]);
      }
    } catch (err) {
      console.error('Follow/unfollow error:', err);
    }
  };

  const handleClickOutside = (e) => {
    if (!e.target.closest('.search-results-container') && 
        !e.target.closest('.search-input')) {
      setShowResults(false);
    }
  };

  useEffect(() => {
    // Auto-search for users when typing in user search mode
    if (searchMode === 'user' && query.trim().length >= 3) {
      const timer = setTimeout(() => {
        handleUserSearch();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [query, searchMode]);

  useEffect(() => {
    // Add event listener for clicks outside the search results
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to check if a user is being followed
  const isFollowingUser = (id) => followingIds.includes(id);

  return (
    <div className="search-bar">
      <div className="search-input-container">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={`Search by ${searchMode}`}
          className="search-input"
          onKeyDown={handleKeyDown}
          onFocus={e => {
            if (onFocus) onFocus(e); // <-- Call the prop to notify HomePage
            if (searchMode === 'user' && query.trim().length >= 3) setShowResults(true);
          }}
        />
        <button
          onClick={handleSearch}
          className="search-button"
          disabled={loading || searchingUsers}
        >
          {loading || searchingUsers ? 'Searching...' : 'Search'}
        </button>
      </div>
      
      <div className="search-mode-buttons">
        <button
          className={`search-mode-button ${searchMode === 'title' ? 'active' : ''}`}
          onClick={() => setSearchMode('title')}
        >
          🔤 Title
        </button>
        <button
          className={`search-mode-button ${searchMode === 'author' ? 'active' : ''}`}
          onClick={() => setSearchMode('author')}
        >
          ✍️ Author
        </button>
        <button
          className={`search-mode-button ${searchMode === 'user' ? 'active' : ''}`}
          onClick={() => setSearchMode('user')}
        >
          👤 Users
        </button>
      </div>
      
      {showResults && searchMode === 'user' && (
        <div className="search-results-container">
          {searchResults.length > 0 ? (
            searchResults.map((user) => (
              <div className="user-item" key={user.id}>
                <img
                  src={user.profile_pic || '/resources/blank-profile.webp'}
                  alt={user.username}
                  className="user-avatar"
                />
                <span className="username">@{user.username}</span>
                <div className="user-actions">
                  <button
                    className="view-profile-btn"
                    onClick={() => {
                      navigate(`/profile/${user.id}`);
                      setShowResults(false);
                    }}
                  >
                    View Profile
                  </button>
                  <button
                    className={`follow-btn ${isFollowingUser(user.id) ? 'following' : ''}`}
                    onClick={() => handleFollowToggle(user.id)}
                  >
                    {isFollowingUser(user.id) ? 'Following' : '+ Follow'}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">No users found</div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;